import { RenewalSummary } from '@policyvision/contracts';
import {
  ArrowLeft,
  ExternalLink,
  FileSpreadsheet,
  PrinterIcon,
} from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';

import { Button } from '@/common/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/common/components/ui/sheet';
import { Skeleton } from '@/common/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/common/components/ui/tooltip';
import ComparatorList from '@/comparator/component/ComparatorList';
import { useExportComparatorExcel } from '@/comparator/hooks/useExportComparatorExcel';
interface IComparisonDrawerProps {
  renewal: RenewalSummary | null;
  isOpen: boolean;
  onClose: () => void;
}

const ComparisonDrawer = ({
  renewal,
  isOpen,
  onClose,
}: IComparisonDrawerProps) => {
  const { t } = useTranslation();
  const PDFContentRef = useRef<HTMLTableElement | null>(null);
  const { exportExcel, isExporting } = useExportComparatorExcel();

  const handlePrintPDF = useReactToPrint({
    contentRef: PDFContentRef,
    bodyClass: 'print:p-4',
  });

  return (
    <Sheet open={isOpen} modal={false}>
      <SheetContent
        className="w-full sm:max-w-232 p-0 top-13.75 h-[calc(100vh-55px)]"
        hideDefaultX
        hideOverlay
      >
        {renewal == null ? (
          <>
            <SheetTitle className="sr-only">
              {t('renewal.comparisonDrawer')}
            </SheetTitle>
            <Skeleton className="h-full" />
          </>
        ) : (
          <div className="flex flex-col h-full">
            <SheetHeader className="p-4 pb-0 bg-terre-600 text-white">
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="default"
                  size="icon"
                  onClick={onClose}
                  aria-label="Close"
                  className="bg-terra-cotta-100 hover:bg-terra-cotta-200"
                >
                  <ArrowLeft className="h-4 w-4 stroke-secondary-foreground" />
                </Button>

                <div className="flex gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="default"
                          size="icon"
                          onClick={() =>
                            exportExcel(
                              renewal.uuid,
                              `${renewal.policyNumber}-comparateur.xlsx`,
                            )
                          }
                          disabled={isExporting}
                          className="bg-terra-cotta-100 hover:bg-terra-cotta-200"
                        >
                          <FileSpreadsheet className="h-4 w-4 stroke-secondary-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('renewal.drawer.downloadExcel')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handlePrintPDF}
                          className="bg-terra-cotta-100 hover:bg-terra-cotta-200"
                        >
                          <PrinterIcon className="h-4 w-4 stroke-secondary-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('renewal.drawer.print')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              <div className="pb-4">
                <SheetTitle className="text-2xl font-bold mb-4 text-white">
                  {renewal.enterpriseName}
                </SheetTitle>
                <div className="flex gap-2 text-sm font-semibold">
                  <span className="px-2 flex items-center gap-2 bg-terre-500 rounded-sm">
                    {t('renewal.type')} : {renewal.type}
                  </span>
                  <span className="px-2 flex items-center gap-2 bg-terre-500 rounded-sm">
                    {t('renewal.insurer')} : {renewal.insurerName}
                  </span>
                  <a
                    className="px-2 flex items-center gap-2 cursor-pointer bg-terre-500 rounded-sm"
                    href="#"
                    target="_blank"
                  >
                    {t('billing.policyNumber')} : {renewal.policyNumber}
                    <ExternalLink className="stroke-2 w-4" />
                  </a>
                </div>
                <p className="italic text-xxs text-secondary pt-4">
                  {t('renewal.comparator.usageWarning')}
                </p>
              </div>
            </SheetHeader>
            <ComparatorList
              renewalUuid={renewal.uuid}
              contentRef={PDFContentRef}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ComparisonDrawer;
